import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  useTheme, 
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import { apiService } from '../services/api';
import { MAIN_TOP_PADDING, HOME_HERO_CONTENT_INSET } from '../constants/layout';

const DEFAULT_HERO_TITLE = 'Stop losing money to slow systems, messy data, and manual work';
const DEFAULT_HERO_SUBTITLE =
  'Boldcomp builds software, AI tools, and automation for NGOs, SMEs, institutions, and campaigns in Kenya. You get clearer reporting, faster operations, and better control — so your team spends time on growth, not spreadsheets.';
const DEFAULT_HERO_CHIP = 'Nairobi & East Africa — custom software, AI & automation for teams that run on results';
const DEFAULT_HERO_STATS = [
  { number: '50+', label: 'projects shipped for Kenyan teams' },
  { number: '24/7', label: 'systems built to run without babysitting' },
  { number: '100%', label: 'owned by you — no lock-in to our stack' },
];
const DEFAULT_WHAT_WE_DELIVER = [
  'Cut admin hours by automating repeat tasks (reports, approvals, reminders).',
  'See what is happening in the field — stock, voters, donors, or projects — in one dashboard.',
  'Turn scattered Excel and WhatsApp chaos into one reliable system your staff can actually use.',
  'Launch and track campaigns (SMS, teams, targets) without losing control of data or budget.',
  'Add AI where it pays: faster responses, smarter routing, less manual typing — not hype slides.',
];
const DEFAULT_EFFECTIVE_SOLUTIONS = [
  {
    title: 'Campaign command for Kenyan politics',
    desc: 'One place for voter lists, teams, messaging, spend, and daily numbers. Built for county, ward, and national teams who cannot afford guesswork on election day.',
    link: '/services',
  },
  {
    title: 'Operations software for growing SMEs',
    desc: 'POS, inventory, branches, and sales tracking that match how you actually work in Nairobi and beyond — not a foreign template.',
    link: '/services',
  },
  {
    title: 'Donor & program systems for NGOs',
    desc: 'Grants, beneficiaries, M&E, and reporting that auditors and donors can follow without last-minute panic.',
    link: '/services',
  },
  {
    title: 'AI & automation that save payroll',
    desc: 'Chatbots, document handling, and workflows that cut repetitive work so your team focuses on revenue and delivery.',
    link: '/ai-solutions',
  },
];
const DEFAULT_STATS_BAR = [
  { number: '5+', label: 'years building for East African clients' },
  { number: '30+', label: 'active systems in production use' },
  { number: '48h', label: 'typical first response on new enquiries' },
  { number: '1', label: 'dedicated lead — no endless handoffs' },
];
const Home = () => {
  const theme = useTheme();
  const [homeData, setHomeData] = useState(null);
  const [homeContent, setHomeContent] = useState(null);
  const [services, setServices] = useState([]);
  const [heroConfig, setHeroConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [heroBgIndex, setHeroBgIndex] = useState(0);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchHomeData();
  }, []);

  useEffect(() => {
    const fetchHero = async () => {
      try {
        const res = await apiService.getHero();
        if (res?.success && res.hero) setHeroConfig(res.hero);
      } catch (e) { /* use defaults */ }
    };
    
    const fetchClientsData = async () => {
      try {
        const data = await apiService.getClients();
        if (Array.isArray(data)) setClients(data);
      } catch (e) {
        console.error(e);
      }
    };
    
    fetchHero();
    fetchClientsData();
  }, []);

  useEffect(() => {
    if (!heroConfig?.hero_images?.length) return;
    const interval = (heroConfig.rotation_interval_seconds || 5) * 1000;
    const t = setInterval(() => {
      setHeroBgIndex((i) => (i + 1) % heroConfig.hero_images.length);
    }, interval);
    return () => clearInterval(t);
  }, [heroConfig]);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 Fetching home data from API...');
      
      // Try to get home data from API
      const response = await apiService.getHomeData();
      console.log('📦 Home API Response:', response);
      
      if (response && response.success !== false) {
        setHomeData(
          response.page || {
            hero_title: DEFAULT_HERO_TITLE,
            hero_subtitle: DEFAULT_HERO_SUBTITLE,
          }
        );
        setServices(Array.isArray(response.services) ? response.services : []);
        setHomeContent(response.home_content || null);
      } else {
        throw new Error(response?.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('❌ Error fetching home data:', error);
      
      // If home API fails, try to get services directly
      try {
        console.log('🔄 Falling back to services API...');
        const servicesResponse = await apiService.getServices();
        console.log('📦 Services API Response:', servicesResponse);
        
        if (Array.isArray(servicesResponse)) {
          setServices(servicesResponse);
        } else if (servicesResponse && Array.isArray(servicesResponse.data)) {
          setServices(servicesResponse.data);
        } else {
          setServices([]);
        }
        
        setHomeData({
          hero_title: DEFAULT_HERO_TITLE,
          hero_subtitle: DEFAULT_HERO_SUBTITLE,
        });
        setHomeContent(null);
        setError('');
      } catch (servicesError) {
        console.error('❌ Services API also failed:', servicesError);
        setError('Unable to load data. Please check your backend connection.');
        setServices([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (iconName) => {
    const iconProps = { sx: { fontSize: 26, color: 'primary.main' } };
    switch(iconName) {
      case 'code': return <CodeIcon {...iconProps} />;
      case 'mobile': return <SmartphoneIcon {...iconProps} />;
      case 'ai': return <PsychologyIcon {...iconProps} />;
      case 'automation': return <AutoAwesomeIcon {...iconProps} />;
      case 'consultancy': return <BusinessCenterIcon {...iconProps} />;
      case 'design': return <DesignServicesIcon {...iconProps} />;
      default: return <CodeIcon {...iconProps} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="primary">
          Loading Boldcomp Solutions...
        </Typography>
      </Box>
    );
  }

  const heroHasImage = heroConfig?.hero_images?.length > 0;

  return (
    <Box
      sx={{
        marginTop: `calc(-1 * ${MAIN_TOP_PADDING})`,
        paddingTop: `calc(${MAIN_TOP_PADDING} + ${HOME_HERO_CONTENT_INSET})`,
      }}
    >
      {/* Hero — editorial B2B: light surface, navy type; optional CMS image as restrained band */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#f4f5f7',
          backgroundImage: heroHasImage
            ? `linear-gradient(105deg, rgba(248,250,252,0.97) 0%, rgba(248,250,252,0.88) 42%, rgba(248,250,252,0.55) 100%), url(${heroConfig.hero_images[heroBgIndex]})`
            : `linear-gradient(180deg, #f8fafc 0%, ${theme.palette.background.default} 55%)`,
          backgroundSize: heroHasImage ? 'cover, cover' : 'auto',
          backgroundPosition: heroHasImage ? 'center, center' : 'auto',
          backgroundRepeat: 'no-repeat',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: 'background-image 0.7s ease',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: { xs: 0, md: '4px' },
            bgcolor: 'secondary.main',
            display: { xs: 'none', md: 'block' },
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 4, md: 6 }, pb: { xs: 5, md: 7 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                {error ? (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                ) : null}

                <Typography
                  component="p"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  {homeContent?.hero_chip_text || DEFAULT_HERO_CHIP}
                </Typography>

                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem' },
                    lineHeight: 1.18,
                    letterSpacing: '-0.03em',
                    mb: 2.5,
                    maxWidth: 560,
                  }}
                >
                  {(heroConfig?.hero_title ?? homeData?.hero_title) || DEFAULT_HERO_TITLE}
                </Typography>

                <Typography
                  variant="body1"
                  component="p"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '1.0625rem', md: '1.125rem' },
                    lineHeight: 1.7,
                    mb: 4,
                    maxWidth: 520,
                  }}
                >
                  {(heroConfig?.hero_subtitle ?? homeData?.hero_subtitle) || DEFAULT_HERO_SUBTITLE}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 3, sm: 5 },
                    mb: 4,
                    flexWrap: 'wrap',
                    py: 2,
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                  }}
                >
                  {(Array.isArray(homeContent?.hero_stats) && homeContent.hero_stats.length > 0
                    ? homeContent.hero_stats
                    : DEFAULT_HERO_STATS
                  ).map((stat, index) => (
                    <Box key={index}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                        {stat.number}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', maxWidth: 140, lineHeight: 1.4 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    component={Link}
                    to={heroConfig?.cta_link || '/contact'}
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 20 }} />}
                    sx={{
                      px: 3,
                      py: 1.25,
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: '0 4px 14px rgba(29, 29, 78, 0.2)' },
                    }}
                  >
                    {heroConfig?.cta_text || 'Book a 15-minute call on WhatsApp'}
                  </Button>

                  <Button
                    component={Link}
                    to="/portfolio"
                    variant="text"
                    size="large"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
                    }}
                  >
                    View client work
                  </Button>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 }}>
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: { xs: 3, md: 3.5 },
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}>
                    Delivery standard
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1, mb: 2.5, fontWeight: 700, color: 'primary.main' }}>
                    What you get when we ship
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(Array.isArray(homeContent?.what_we_deliver) && homeContent.what_we_deliver.length > 0
                      ? homeContent.what_we_deliver
                      : DEFAULT_WHAT_WE_DELIVER
                    ).map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 22, mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                          {typeof item === 'string' ? item : item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Client Logos Strip */}
      {clients && clients.length > 0 && (
        <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth="lg">
            <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', mb: 4 }}>
              Trusted by leading organizations
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: { xs: 4, md: 6, lg: 8 },
              opacity: 0.7,
              '&:hover': { opacity: 1 },
              transition: 'opacity 0.3s ease'
            }}>
              {clients.map((client) => (
                <Box key={client.id} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  filter: 'grayscale(100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { filter: 'grayscale(0%)', transform: 'translateY(-2px)' }
                }}>
                  {client.logo_path ? (
                    <img 
                      src={`/boldcomp-solutions/${client.logo_path}`} 
                      alt={client.name} 
                      title={client.name}
                      style={{ maxHeight: '48px', maxWidth: '140px', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.disabled' }}>
                      {client.name}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Key solutions */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{ color: 'primary.main', fontWeight: 700, mb: 1.5, fontSize: { xs: '1.65rem', md: '2rem' } }}
            >
              Problems we fix for Kenyan teams
            </Typography>
            <Typography variant="body1" sx={{ mb: 5, color: 'text.secondary', maxWidth: 640, lineHeight: 1.7 }}>
              NGOs, SMEs, campaigns, and institutions — if it runs on people and data, we build the system behind it. Tell us what is broken; we scope fast.
            </Typography>
            <Grid container spacing={3}>
              {(Array.isArray(homeContent?.effective_solutions) && homeContent.effective_solutions.length > 0 ? homeContent.effective_solutions : DEFAULT_EFFECTIVE_SOLUTIONS).map((item, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        borderColor: 'rgba(14, 139, 199, 0.35)',
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.35 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                        {item.desc}
                      </Typography>
                      <Button component={Link} to={item.link} size="small" color="secondary" sx={{ fontWeight: 600, p: 0, minWidth: 0 }}>
                        Learn more
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Trust metrics — track record band */}
      <Box
        component="section"
        aria-label="Track record"
        sx={{
          position: 'relative',
          py: { xs: 6, md: 7 },
          overflow: 'hidden',
          color: 'rgba(255,255,255,0.95)',
          background: `linear-gradient(168deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 42%, #16184a 100%)`,
          borderTop: '1px solid rgba(255,255,255,0.07)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(14, 139, 199, 0.18), transparent 55%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            component="p"
            textAlign="center"
            sx={{
              display: 'block',
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 700,
              letterSpacing: '0.14em',
              mb: 1,
            }}
          >
            Track record
          </Typography>
          <Typography
            variant="body2"
            textAlign="center"
            sx={{
              color: 'rgba(255,255,255,0.72)',
              maxWidth: 520,
              mx: 'auto',
              mb: { xs: 4, md: 5 },
              lineHeight: 1.65,
            }}
          >
            Real delivery metrics — not vanity counts. Built for teams that need systems that stay in production.
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {(Array.isArray(homeContent?.stats_bar) && homeContent.stats_bar.length > 0 ? homeContent.stats_bar : DEFAULT_STATS_BAR).map((stat, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      p: { xs: 2, sm: 2.25 },
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderLeftWidth: 3,
                      borderLeftColor: 'secondary.main',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      textAlign: { xs: 'left', md: 'left' },
                      transition: 'background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.09)',
                        borderColor: 'rgba(255,255,255,0.14)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography
                      component="p"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.85rem', sm: '2.15rem' },
                        lineHeight: 1.05,
                        letterSpacing: '-0.03em',
                        color: '#fff',
                        fontFeatureSettings: '"tnum"',
                        mb: 1.25,
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Box
                      sx={{
                        width: 36,
                        height: 3,
                        borderRadius: 1,
                        bgcolor: 'secondary.main',
                        opacity: 0.9,
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{
                        color: 'rgba(255,255,255,0.82)',
                        lineHeight: 1.55,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        fontWeight: 500,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
          <Box sx={{ mb: 5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}>
              Capabilities
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                fontSize: { xs: '1.65rem', md: '2rem' },
                mt: 1,
                mb: 2,
                maxWidth: 720,
              }}
            >
              {homeContent?.services_heading || 'Software and AI that move your numbers — not just your website'}
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640, lineHeight: 1.7 }}>
              {homeContent?.services_subtitle ||
                'From M-Pesa-ready workflows to field data and board-ready reports, we design for power cuts, real users, and tight budgets. You tell us the bottleneck; we ship the system.'}
            </Typography>

            {services.length === 0 && !loading && (
              <Alert severity="info" sx={{ mt: 3, maxWidth: 520 }}>
                No services available. Please add services through the admin panel.
              </Alert>
            )}
          </Box>

          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={service.id || index}>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.24) }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'left',
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        borderColor: 'rgba(14, 139, 199, 0.3)',
                        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: '0 !important' }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'rgba(29, 29, 78, 0.12)',
                          bgcolor: 'rgba(29, 29, 78, 0.03)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2.5,
                        }}
                      >
                        {getServiceIcon(service.icon)}
                      </Box>

                      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5, fontSize: '1.1rem' }}>
                        {service.title}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 2 }}>
                        {service.short_description || service.description}
                      </Typography>

                      {service.featured ? (
                        <Chip label="Often requested" size="small" variant="outlined" sx={{ borderColor: 'divider', fontWeight: 600 }} />
                      ) : null}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {services.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <Button component={Link} to="/services" variant="outlined" color="primary" size="large" endIcon={<ArrowForwardIcon />}>
                All services
              </Button>
            </Box>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default Home;